import type {
  DataExplorerDisplayMediaItem,
  DataExplorerDisplayMediaProps,
} from './types';
import { cn } from './utils';

function normalizeMedia<TSource>(
  media?:
    | DataExplorerDisplayMediaItem<TSource>
    | Array<DataExplorerDisplayMediaItem<TSource>>,
) {
  if (!media) {
    return [];
  }
  return Array.isArray(media) ? media.filter(Boolean) : [media];
}

function resolveMediaKind(value?: string) {
  return String(value || 'unknown').toLowerCase();
}

export function DataExplorerDisplayMedia<TSource = unknown>({
  media,
  fallbackTitle,
  empty,
  className,
  classNames,
  onOpen,
}: DataExplorerDisplayMediaProps<TSource>) {
  const mediaItems = normalizeMedia(media);
  const primary = mediaItems[0];

  if (!primary) {
    if (!empty) {
      return null;
    }
    return (
      <div
        className={cn(
          'flex min-h-24 items-center justify-center bg-muted/30 text-sm text-muted-foreground',
          classNames?.placeholder,
          className,
        )}
      >
        {empty}
      </div>
    );
  }

  if (primary.node) {
    return (
      <div
        className={cn('min-w-0 overflow-hidden', classNames?.root, className)}
      >
        {primary.node}
      </div>
    );
  }

  const kind = resolveMediaKind(primary.kind);
  const src = String(primary.src || primary.thumbnail || '').trim();
  const title = primary.title ?? fallbackTitle;
  const alt = primary.alt || (typeof title === 'string' ? title : 'preview');
  const canOpen = typeof onOpen === 'function';
  const mediaNode = (() => {
    if (!src) {
      return (
        <div
          className={cn(
            'flex min-h-24 items-center justify-center bg-muted/30 text-sm text-muted-foreground',
            classNames?.placeholder,
          )}
        >
          {title || 'No preview'}
        </div>
      );
    }

    if (kind === 'image' || kind === 'unknown') {
      return (
        <img
          src={src}
          alt={alt}
          className={cn('h-auto w-full object-contain', classNames?.media)}
          loading="lazy"
          decoding="async"
          draggable={false}
        />
      );
    }

    if (kind === 'video') {
      return (
        <video
          src={src}
          className={cn('h-auto w-full', classNames?.media)}
          controls
          preload="metadata"
        />
      );
    }

    if (kind === 'audio') {
      return (
        <audio
          src={src}
          className={cn('w-full', classNames?.media)}
          controls
          preload="metadata"
        />
      );
    }

    return (
      <div
        className={cn(
          'flex min-h-24 flex-col items-center justify-center gap-1 bg-muted/30 p-4 text-center text-sm text-muted-foreground',
          classNames?.placeholder,
        )}
      >
        {title ? (
          <span className={cn('max-w-full truncate', classNames?.title)}>
            {title}
          </span>
        ) : null}
        <span className="max-w-full truncate">{src}</span>
      </div>
    );
  })();

  if (!canOpen) {
    return (
      <div
        className={cn('min-w-0 overflow-hidden', classNames?.root, className)}
      >
        {mediaNode}
      </div>
    );
  }

  return (
    <button
      type="button"
      className={cn(
        'block min-w-0 overflow-hidden text-left',
        classNames?.root,
        className,
      )}
      onClick={() => onOpen?.(primary, 0)}
    >
      {mediaNode}
    </button>
  );
}
